"use client";

import { RequestDemoData } from "@/types/data";
import RequestDemoForm from "./RequestDemoForm";
import Image from "next/image";
import { GeneralData } from "@/types/components";
import { useEffect, useRef } from "react";
import { gsap } from "@/lib/gsap";

export default function RequestDemoSection({
  data,
  generalData,
}: {
  data: RequestDemoData["content"];
  generalData: GeneralData["content"];
}) {
  const { tagline, title, description, items, formTitle } = data;
  const { working_hours_short_version } = generalData;

  
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const itemsRef = useRef<HTMLUListElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  
  useEffect(() => {
    const headerElement = headerRef.current;
    const taglineElement = taglineRef.current;
    const titleElement = titleRef.current;
    const descriptionElement = descriptionRef.current;
    const itemsElement = itemsRef.current;
    const formElement = formRef.current;

    if (!headerElement) return;

    const ctx = gsap.context(() => {
      
      gsap.set(headerElement, {
        opacity: 0,
        filter: "blur(8px)",
        y: 20,
      });

      if (taglineElement) {
        gsap.set(taglineElement, {
          opacity: 0,
          y: 20,
          filter: "blur(8px)",
        });
      }

      if (titleElement) {
        gsap.set(titleElement, {
          opacity: 0,
          y: 30,
          filter: "blur(8px)",
        });
      }

      if (descriptionElement) {
        gsap.set(descriptionElement, {
          opacity: 0,
          y: 20,
          filter: "blur(8px)",
        });
      }

      
      const headerTl = gsap.timeline({
        scrollTrigger: {
          trigger: headerElement,
          start: "top 85%",
          end: "top 60%",
          toggleActions: "play none none reverse",
        },
      });

      
      headerTl.to(headerElement, {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        duration: 0.6,
        ease: "power2.out",
      });

      if (taglineElement) {
        headerTl.to(
          taglineElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.4"
        );
      }

      if (titleElement) {
        headerTl.to(
          titleElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.8,
            ease: "power2.out",
          },
          "-=0.6"
        );
      }

      if (descriptionElement) {
        headerTl.to(
          descriptionElement,
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.6,
            ease: "power2.out",
          },
          "-=0.4"
        );
      }

      
      if (itemsElement) {
        const itemElements = itemsElement.children;

        
        gsap.set(itemElements, {
          opacity: 0,
          y: 40,
          filter: "blur(8px)",
        });

        
        gsap.to(itemElements, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.2, 
          scrollTrigger: {
            trigger: itemsElement,
            start: "top 85%",
            end: "top 60%",
            toggleActions: "play none none reverse",
          },
        });
      } else {
        console.log("Items element not found");
      }

      
      if (formElement) {
        gsap.set(formElement, {
          opacity: 0,
          filter: "blur(8px)",
          y: 30,
        });

        gsap.to(formElement, {
          opacity: 1,
          filter: "blur(0px)",
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: formElement,
            start: "top 80%",
            end: "top 40%",
            toggleActions: "play none none reverse",
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} id="demo-form" className="container py-[70px]">
      <div className="flex flex-col gap-12 items-center">
        {}
        <div className="w-full">
          <div ref={headerRef} className="text-center mb-12 opacity-0">
            {tagline && (
              <div
                ref={taglineRef}
                className="flex items-center justify-center mb-4 opacity-0"
              >
                <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
                <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                  {tagline}
                </p>
              </div>
            )}
            {title && (
              <h2
                ref={titleRef}
                className="text-4xl md:text-5xl font-bold text-theme-secondary opacity-0"
              >
                {title}
              </h2>
            )}
            {description && (
              <p
                ref={descriptionRef}
                className="mt-4 text-lg text-theme-secondary-light opacity-0"
              >
                {description}
              </p>
            )}
          </div>

          {items && items.length > 0 && (
            <ul
              ref={itemsRef}
              className=" flex flex-col md:flex-row gap-6   md:items-start items-center "
            >
              {items &&
                items.length > 0 &&
                items.map((item, index: number) => (
                  <li
                    key={index}
                    className="flex flex-col justify-center items-center text-center opacity-0"
                  >
                    <div className=" mb-2">
                      {item.icon.url && (
                        <Image
                          src={item.icon.url}
                          alt={item.icon.alt}
                          width={20}
                          height={20}
                          quality={90}
                          className="object-contain flex-shrink-0  text-[#385180] "
                        />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[20px] leading-[28px] text-[#284D93]">
                        {item.title}
                      </h3>
                      <p className="text-[#4B5563] text-[16px] leading-[24px] max-md:w-[320px]">
                        {item.description}
                      </p>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>

        {}
        <div ref={formRef} className="w-full opacity-0">
          <RequestDemoForm
            formTitle={formTitle}
            working_hours_short_version={working_hours_short_version}
          />
        </div>
      </div>
    </section>
  );
}
